import { useNavigate } from 'react-router-dom';
import {
  Card,
  Text,
  BlockStack,
  Spinner,
  Banner,
  EmptyState,
} from '@shopify/polaris';
import { useSites } from '../hooks/useSites';
import { Layout } from '../components/Layout';

export function ReportsSiteSelection() {
  const navigate = useNavigate();
  const { data: sites, isLoading, error } = useSites();

  // Filter only GA4-enabled sites
  const ga4Sites = sites?.filter((site) => site.has_ga4_property) || [];

  const handleSiteClick = (siteId: string, googleConnected: boolean) => {
    if (googleConnected) {
      navigate(`/site/${siteId}/standard-reports`);
    } else {
      navigate(`/site/${siteId}`);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
          <Spinner size="large" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <BlockStack gap="400">
        {/* Page Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <BlockStack gap="100">
            <Text as="h1" variant="headingLg" fontWeight="bold">
              Standard Reports
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Select a site to view analytics reports
            </Text>
          </BlockStack>
        </div>

        {error && (
          <Banner tone="critical">
            Failed to load sites. Please try again.
          </Banner>
        )}

        {ga4Sites.length === 0 ? (
          <Card>
            <EmptyState
              heading="No connected sites"
              image=""
              action={{
                content: 'Go to Dashboard',
                onAction: () => navigate('/dashboard'),
              }}
            >
              <p>
                You don't have any sites with Google Analytics connected yet.
                Go to the Dashboard to set up GA4 for your sites.
              </p>
            </EmptyState>
          </Card>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '20px',
            }}
          >
            {ga4Sites.map((site) => (
              <div
                key={site.id}
                onClick={() => handleSiteClick(site.id, site.google_connected)}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  border: '1px solid #e1e3e5',
                  cursor: 'pointer',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)';
                }}
              >
                {/* Site Preview Image */}
                <div
                  style={{
                    height: '160px',
                    backgroundColor: '#f6f6f7',
                    backgroundImage: site.preview_url
                      ? `url(${site.preview_url})`
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    position: 'relative',
                  }}
                >
                  {/* Status Badge */}
                  <div style={{ position: 'absolute', top: '12px', right: '12px' }}>
                    <span
                      style={{
                        backgroundColor: site.google_connected ? '#b4fed5' : '#ffd79d',
                        color: site.google_connected ? '#0d6832' : '#8a6116',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 500,
                      }}
                    >
                      {site.google_connected ? 'Connected' : 'Reconnect Required'}
                    </span>
                  </div>
                </div>

                {/* Site Info */}
                <div style={{ padding: '16px' }}>
                  <Text as="h3" variant="headingMd" fontWeight="semibold">
                    {site.display_name}
                  </Text>

                  {site.measurement_id && (
                    <Text as="p" variant="bodySm" tone="subdued">
                      {site.measurement_id}
                    </Text>
                  )}

                  {/* Action Button */}
                  <div style={{ marginTop: '16px' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSiteClick(site.id, site.google_connected);
                      }}
                      style={{
                        backgroundColor: site.google_connected ? '#5c6ac4' : '#f4a21b',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        width: '100%',
                        transition: 'background-color 0.15s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = site.google_connected ? '#4959bd' : '#d68f18';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = site.google_connected ? '#5c6ac4' : '#f4a21b';
                      }}
                    >
                      {site.google_connected ? 'View Report' : 'Setup'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </BlockStack>
    </Layout>
  );
}
